import { TextInputCellRenderer, type TypeTableColumn } from '@thefarmersfront/kitchen';

import {
  ReceiveRequestDateCell,
  ReceiveRequestQuantityInputCell,
  ReceiveRequestSelectCell,
} from '~/components/receive';

import { formatNumber } from '~/utils/common';

import { LOT_NO_MAX_LENGTH, LOT_NO_REGEX } from '~/constants';

import type { TypeReceiveItemEditableFields, TypeReceiveRequestRegisterModalGoods } from '~/types/receive.types';

const QUANTITY_TOOLTIP = '수량(BOX) 또는 수량(EA)중\n하나는 필수로 입력해야 합니다.';

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > 1페이지 > 입고 상품 테이블
 *
 * 입력 셀이 다시 마운트되지 않도록 목록은 getData 로 그때그때 읽는다(목록을 인자로 받으면 입력할 때마다 컬럼이 바뀐다).
 */
const getReceiveRegisterModalColumnDefs = ({
  hasPermissionNFA,
  getData,
  setData,
}: {
  hasPermissionNFA: boolean;
  getData: () => TypeReceiveRequestRegisterModalGoods[];
  setData: (goodsList: TypeReceiveRequestRegisterModalGoods[]) => void;
}) => {
  const updateLotNo = (rowIndex: number, lotNo: string) => {
    const data = getData();
    setData([...data.slice(0, rowIndex), { ...data[rowIndex], lotNo }, ...data.slice(rowIndex + 1)]);
  };

  const quantityColumn = (
    accessorKey: TypeReceiveItemEditableFields,
    header: string,
    tooltip?: string
  ): TypeTableColumn<TypeReceiveRequestRegisterModalGoods> => ({
    accessorKey,
    header,
    ...(tooltip && { meta: { headerTooltip: { description: tooltip, maxWidth: '171px' } } }),
    cell: ({ row }) => <ReceiveRequestQuantityInputCell rowIndex={row.index} columnId={accessorKey} />,
    size: 120,
    minSize: 120,
  });

  const columnDefs: TypeTableColumn<TypeReceiveRequestRegisterModalGoods>[] = [
    {
      id: 'selection',
      header: '',
      cell: () => null,
      size: 44,
      meta: { selection: true },
    },
    {
      accessorKey: 'goodsCode',
      header: '상품코드',
      size: 140,
    },
    ...(hasPermissionNFA
      ? [
          {
            accessorKey: 'nsid',
            header: 'NSID',
            size: 180,
          } satisfies TypeTableColumn<TypeReceiveRequestRegisterModalGoods>,
        ]
      : []),
    {
      accessorKey: 'alternativeCode',
      header: '상품바코드',
      size: 150,
    },
    {
      accessorKey: 'goodsName',
      header: '상품명',
      minSize: 240,
      meta: { flex: true },
    },
    {
      accessorKey: 'kurlyKeepTypeDesc',
      header: '보관유형',
      size: 100,
    },
    {
      id: 'dateUse',
      header: '기한/일자 사용여부',
      meta: {
        headerTooltip: {
          description: '유통/소비기한 또는 제조일자가 없다면\n[사용] 체크박스를 해제해 주세요.',
          maxWidth: '203px',
        },
      },
      cell: ({ row }) => <ReceiveRequestDateCell rowIndex={row.index} />,
      size: 309,
      minSize: 309,
    },
    {
      accessorKey: 'palletYn',
      header: '파렛트 사용여부',
      meta: {
        headerTooltip: {
          description: '상품을 파렛트에 적재 후 입고하면\n[사용함]으로 선택해 주세요.',
          maxWidth: '182px',
        },
      },
      cell: ({ row }) => <ReceiveRequestSelectCell rowIndex={row.index} columnId="palletYn" />,
      size: 136,
      minSize: 136,
    },
    {
      accessorKey: 'lotNo',
      header: 'LOT 번호',
      meta: {
        headerTooltip: {
          description:
            '상품의 제조 또는 생산 단위를 구분하기 위한 고유 식별 번호입니다.\n입력된 LOT 번호는 재고 추적 및 이력 관리에 활용됩니다.',
          maxWidth: '335px',
        },
      },
      cell: ({ getValue, row }) => (
        <TextInputCellRenderer
          type="text"
          value={getValue<TypeReceiveRequestRegisterModalGoods['lotNo']>() ?? ''}
          placeholder={`${LOT_NO_MAX_LENGTH}자 이하 입력`}
          maxLength={LOT_NO_MAX_LENGTH}
          onChange={(e) => {
            if (!LOT_NO_REGEX.test(e.target.value)) return;
            updateLotNo(row.index, e.target.value);
          }}
          onBlur={(e) => updateLotNo(row.index, e.target.value.trim())}
        />
      ),
      size: 200,
      minSize: 200,
    },
    quantityColumn('boxQty', '수량(BOX)', QUANTITY_TOOLTIP),
    quantityColumn('boxPerQty', '입수(BOX당)'),
    quantityColumn('eachQty', '수량(EA)', QUANTITY_TOOLTIP),
    {
      id: 'totalQty',
      accessorFn: (data) => data.totalQty,
      header: '총 수량',
      meta: {
        cellStyle: { textAlign: 'right' },
        headerTooltip: { description: '수량(BOX)*입수(BOX당)+수량(EA)로\n자동 계산됩니다.', maxWidth: '210px' },
      },
      cell: ({ getValue }) => {
        const totalQty = getValue<number | null>();
        return totalQty ? formatNumber(totalQty) : '-';
      },
      size: 112,
    },
  ];

  return { columnDefs };
};

export default getReceiveRegisterModalColumnDefs;
